import React, { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Loader2, Send, X, Copy, CheckCircle2, AlertCircle, Clock, BadgePercent, Plus,
} from "lucide-react";
import {
  adminCreatePaymentLink, adminListPaymentLinks, adminResendPaymentLink,
  adminCancelPaymentLink, adminListCustomers, fetchContent,
} from "../../lib/api";

const statusColor = (s) => ({
  pending: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
  cancelled: "bg-gray-200 text-gray-700",
  expired: "bg-red-100 text-red-700",
}[s] || "bg-gray-100 text-gray-600");

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

const blankForm = {
  customer_email: "",
  customer_name: "",
  customer_phone: "",
  plan_key: "",
  billing_months: 12,
  domain_name: "",
  locked_renewal_price: "",
  send_email: true,
  notes: "",
};

export default function PayLinksPanel({ onToast }) {
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState({});
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, c, cms] = await Promise.all([
        adminListPaymentLinks(statusFilter || undefined),
        adminListCustomers(),
        fetchContent(),
      ]);
      setLinks(l || []);
      setCustomers(c || []);
      setPlans((cms?.plans) || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load pay links", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.key === form.plan_key),
    [plans, form.plan_key],
  );
  const tiers = (selectedPlan?.pricing_tiers) || [];
  const selectedTier = tiers.find((t) => Number(t.months) === Number(form.billing_months));

  const onPickCustomer = (email) => {
    const c = customers.find((x) => (x.email || "").toLowerCase() === (email || "").toLowerCase());
    setForm((f) => ({
      ...f,
      customer_email: email,
      customer_name: c?.name || f.customer_name,
      customer_phone: c?.phone || f.customer_phone,
    }));
  };

  const submit = async () => {
    if (!form.customer_email || !form.plan_key || !form.domain_name) {
      onToast?.("Email, plan and domain are required", "error");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        billing_months: Number(form.billing_months) || 12,
        locked_renewal_price: form.locked_renewal_price === "" ? null : Number(form.locked_renewal_price),
      };
      const r = await adminCreatePaymentLink(payload);
      onToast?.(r.email_sent ? "Pay link created & emailed" : "Pay link created");
      setForm(blankForm);
      setCreating(false);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not create link", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (token) => {
    setBusy((b) => ({ ...b, [token]: true }));
    try {
      const r = await adminResendPaymentLink(token);
      onToast?.(r.email_sent ? `Resent (attempt ${r.attempt})` : "Resend failed", r.email_sent ? "success" : "error");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Resend failed", "error");
    } finally {
      setBusy((b) => ({ ...b, [token]: false }));
    }
  };

  const cancel = async (token) => {
    if (!window.confirm("Cancel this pay link? The customer won't be able to use it anymore.")) return;
    setBusy((b) => ({ ...b, [token]: true }));
    try {
      await adminCancelPaymentLink(token, "admin_cancelled");
      onToast?.("Cancelled");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Cancel failed", "error");
    } finally {
      setBusy((b) => ({ ...b, [token]: false }));
    }
  };

  const copy = async (link) => {
    const url = `${window.location.origin}/checkout?token=${link.token}`;
    try { await navigator.clipboard.writeText(url); setCopied(link.token); setTimeout(() => setCopied(null), 1500); }
    catch { /* ignore */ }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Payment Links</h2>
          <p className="text-[#11151C]/60 text-sm mt-1">
            Generate a one-click Razorpay pay link for an existing customer. We email it instantly and auto-resend daily for up to 3 days if unpaid.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="paylinks-filter"
            className="px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm bg-white"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#11151C]/15 text-sm font-semibold hover:bg-[#FFE6D6]"
            data-testid="paylinks-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setCreating(true)}
            className="lm-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold"
            data-testid="paylinks-create"
          >
            <Plus className="w-4 h-4" /> New pay link
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6] text-[#11151C]/70 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Plan / Domain</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Emails</th>
              <th className="text-left p-3">Expires</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#11151C]/55">No pay links yet.</td></tr>
            )}
            {links.map((l) => (
              <tr key={l.token} className="border-t border-[#11151C]/5 hover:bg-[#FFFBF6]" data-testid={`paylink-row-${l.token}`}>
                <td className="p-3 align-top">
                  <div className="font-semibold text-[#11151C]">{l.customer_name || "—"}</div>
                  <div className="text-xs text-[#11151C]/55 break-all">{l.customer_email}</div>
                </td>
                <td className="p-3 align-top">
                  <div className="font-semibold">{l.plan_name}</div>
                  <div className="text-xs text-[#11151C]/55">{l.domain_name}</div>
                  {l.locked_renewal_price ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B23B12] bg-[#FFE6D6] px-1.5 py-0.5 rounded">
                      <BadgePercent className="w-3 h-3" /> locks ₹{l.locked_renewal_price}
                    </div>
                  ) : null}
                </td>
                <td className="p-3 text-right align-top">
                  <div className="font-bold text-[#11151C]">₹{Number(l.total_inr || 0).toLocaleString("en-IN")}</div>
                  <div className="text-[11px] text-[#11151C]/55">incl. GST</div>
                </td>
                <td className="p-3 align-top">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusColor(l.status)}`}>{l.status}</span>
                </td>
                <td className="p-3 align-top text-xs">
                  <div>{l.email_sent_count || 0} / 3 sent</div>
                  <div className="text-[#11151C]/55">last: {fmtDate(l.last_emailed_at)}</div>
                </td>
                <td className="p-3 align-top text-xs">
                  <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(l.expires_at)}</div>
                </td>
                <td className="p-3 align-top">
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      onClick={() => copy(l)}
                      title="Copy pay URL"
                      className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"
                      data-testid={`paylink-copy-${l.token}`}
                    >
                      {copied === l.token ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-[#11151C]/60" />}
                    </button>
                    {l.status === "pending" && (
                      <>
                        <button
                          disabled={!!busy[l.token]}
                          onClick={() => resend(l.token)}
                          title="Resend email now"
                          className="p-1.5 hover:bg-[#FFE6D6] rounded-lg disabled:opacity-50"
                          data-testid={`paylink-resend-${l.token}`}
                        >
                          {busy[l.token] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 text-[#FF6B3D]" />}
                        </button>
                        <button
                          disabled={!!busy[l.token]}
                          onClick={() => cancel(l.token)}
                          title="Cancel link"
                          className="p-1.5 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          data-testid={`paylink-cancel-${l.token}`}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="paylinks-create-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg">Create payment link</h3>
              <button onClick={() => setCreating(false)}><X className="w-5 h-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Customer email *</span>
                <input
                  list="paylink-customers"
                  value={form.customer_email}
                  onChange={(e) => onPickCustomer(e.target.value)}
                  placeholder="user@example.com"
                  data-testid="paylink-input-email"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
                <datalist id="paylink-customers">
                  {customers.map((c) => <option key={c.id} value={c.email}>{c.name}</option>)}
                </datalist>
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Customer name</span>
                <input
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Phone</span>
                <input
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Plan *</span>
                <select
                  value={form.plan_key}
                  onChange={(e) => setForm({ ...form, plan_key: e.target.value })}
                  data-testid="paylink-input-plan"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm bg-white"
                >
                  <option value="">— pick a plan —</option>
                  {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Billing months</span>
                <select
                  value={form.billing_months}
                  onChange={(e) => setForm({ ...form, billing_months: Number(e.target.value) })}
                  data-testid="paylink-input-months"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm bg-white"
                >
                  {(tiers.length ? tiers : [{ months: 12 }]).map((t) => (
                    <option key={t.months} value={t.months}>
                      {t.months} months {t.price ? `— ₹${t.price.toLocaleString("en-IN")}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Domain *</span>
                <input
                  value={form.domain_name}
                  onChange={(e) => setForm({ ...form, domain_name: e.target.value.toLowerCase().trim() })}
                  placeholder="example.com"
                  data-testid="paylink-input-domain"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
              </label>
              <label className="text-xs">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65 inline-flex items-center gap-1">
                  <BadgePercent className="w-3 h-3" /> Locked renewal ₹
                </span>
                <input
                  type="number"
                  value={form.locked_renewal_price}
                  onChange={(e) => setForm({ ...form, locked_renewal_price: e.target.value })}
                  placeholder="(blank = no lock)"
                  data-testid="paylink-input-locked"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
              </label>
              <label className="text-xs sm:col-span-2">
                <span className="font-bold uppercase tracking-wider text-[#11151C]/65">Notes (internal)</span>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. Phone-call upsell · diwali promo"
                  className="mt-1 w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                />
              </label>
            </div>

            <label className="mt-3 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                data-testid="paylink-input-send-email"
              />
              <span className="text-sm text-[#11151C]/75">Email the link to the customer now</span>
            </label>

            {selectedTier && (
              <div className="mt-3 p-3 rounded-xl bg-[#FFFBF6] border border-[#FF6B3D]/20 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-[#11151C]/70">Customer will pay</span>
                  <span className="font-bold text-[#11151C]">₹{Number(selectedTier.price || 0).toLocaleString("en-IN")} + GST</span>
                </div>
                {form.locked_renewal_price && (
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-[#11151C]/70 inline-flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> Renews forever at</span>
                    <span className="font-bold text-[#B23B12]">₹{Number(form.locked_renewal_price).toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="paylink-submit"
                className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Generate & email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
